import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { useTripDaysServiceV1BatchCreateTripDaysThresholdMutation } from "_services/modules/trip_days";
import { DurationCondition } from "_services/modules/tripDaysThreshold";
import { parseFormattedNumber, yupNumTransform } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface TripDaysItem {
  tripDaysName: string;
  durationCondition: DurationCondition;
  durationValue: number | string;
  durationMin: number | string;
  durationMax: number | string;
  equivalentDays: number | string;
  description: string;
  status: string;
}

export interface AddTripDaysThresholdFormValues {
  tripDaysName: string;
  durationCondition: DurationCondition;
  durationValue: number | string;
  durationMin: number | string;
  durationMax: number | string;
  equivalentDays: number | string;
  description: string;
  status: string;
  items: TripDaysItem[];
}

const schema = yup.object({
  tripDaysName: yup.string().required("Trip Days Name is required").max(100, "Max 100 characters"),
  durationCondition: yup.string().oneOf(["below", "between", "above"]).required("Duration Condition is required"),
  durationValue: yup.mixed().when("durationCondition", {
    is: (val: string) => val === "below" || val === "above",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Duration value is required").positive("Must be positive"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMin: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Min value is required").min(0, "Must be >= 0"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMax: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Max value is required").positive("Must be positive")
      .when("durationMin", (durationMin, schema) => {
        const minVal = Array.isArray(durationMin) ? durationMin[0] : durationMin;
        if (typeof minVal === "number" && !isNaN(minVal)) {
          return schema.moreThan(minVal, "Max must be greater than Min");
        }
        return schema;
      }),
    otherwise: () => yup.mixed().notRequired(),
  }),
  equivalentDays: yup.number().transform(yupNumTransform).typeError("Must be a number").required("Equivalent Days is required").positive("Must be positive"),
  description: yup.string().max(500, "Max 500 characters").default(""),
  status: yup.string().required("Status is required"),
  items: yup.array().of(
    yup.object({
      tripDaysName: yup.string().required(),
      durationCondition: yup.string().required(),
      durationValue: yup.mixed(),
      durationMin: yup.mixed(),
      durationMax: yup.mixed(),
      equivalentDays: yup.mixed(),
      description: yup.string().default(""),
      status: yup.string().required(),
    })
  ).required(),
}).required();

const useAddTripDaysThresholdForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("tripDaysThreshold");

  const [batchCreateTripDaysThreshold] = useTripDaysServiceV1BatchCreateTripDaysThresholdMutation();

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm<AddTripDaysThresholdFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as never,
    defaultValues: {
      tripDaysName: "",
      durationCondition: "below",
      durationValue: "",
      durationMin: "",
      durationMax: "",
      equivalentDays: "",
      description: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const durationCondition = watch("durationCondition");

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const getDurationDisplayText = (item: TripDaysItem): string => {
    const fmt = (v: number | string) => typeof v === "string" ? v.replace(",", ".") : String(v);
    if (item.durationCondition === "below") {
      return `< ${fmt(item.durationValue)} Hours`;
    }
    if (item.durationCondition === "between") {
      return `${fmt(item.durationMin)} Hours - ${fmt(item.durationMax)} Hours`;
    }
    if (item.durationCondition === "above") {
      return `> ${fmt(item.durationValue)} Hours`;
    }
    return "-";
  };

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddTripDaysThresholdFormValues)[] = [
      "tripDaysName",
      "durationCondition",
      "equivalentDays",
      "status",
    ];

    if (durationCondition === "between") {
      fieldsToValidate.push("durationMin", "durationMax");
    } else {
      fieldsToValidate.push("durationValue");
    }

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();

    const isDuplicate = fields.some(
      (item, index) =>
        item.tripDaysName.toLowerCase() === values.tripDaysName.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("tripDaysName", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    const normalizeNum = (val: number | string) =>
      typeof val === "string" && val !== "" ? parseFormattedNumber(val) : val;

    const newItem: TripDaysItem = {
      tripDaysName: values.tripDaysName,
      durationCondition: values.durationCondition,
      durationValue: values.durationCondition !== "between" ? normalizeNum(values.durationValue) : "",
      durationMin: values.durationCondition === "between" ? normalizeNum(values.durationMin) : "",
      durationMax: values.durationCondition === "between" ? normalizeNum(values.durationMax) : "",
      equivalentDays: normalizeNum(values.equivalentDays),
      description: values.description,
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    resetForm();
  };

  const resetForm = () => {
    setValue("tripDaysName", "");
    setValue("durationCondition", "below");
    setValue("durationValue", "");
    setValue("durationMin", "");
    setValue("durationMax", "");
    setValue("equivalentDays", "");
    setValue("description", "");
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    setValue("tripDaysName", item.tripDaysName);
    setValue("durationCondition", item.durationCondition);
    setValue("durationValue", item.durationValue);
    setValue("durationMin", item.durationMin);
    setValue("durationMax", item.durationMax);
    setValue("equivalentDays", item.equivalentDays);
    setValue("description", item.description);
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetForm();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetForm();
  };

  const handleSave = async () => {
    try {
      const payload = fields.map((f) => ({
        trip_days_threshold_name: f.tripDaysName,
        duration_condition: f.durationCondition,
        duration_min: f.durationCondition === "between" ? Number(f.durationMin) : (f.durationCondition === "above" ? Number(f.durationValue) : undefined),
        duration_max: f.durationCondition === "between" ? Number(f.durationMax) : (f.durationCondition === "below" ? Number(f.durationValue) : undefined),
        equivalent_days: Number(f.equivalentDays),
        status: f.status,
      }));
      await batchCreateTripDaysThreshold({
        tripDaysV1BatchCreateTripDaysThresholdReq: {
          trip_days_thresholds: payload,
        },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (fields.length > 0) {
      setShowCancelModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    durationCondition,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    getDurationDisplayText,
    t,
  };
};

export default useAddTripDaysThresholdForm;
